import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { HexAlphaColorPicker } from "react-colorful"

import Button from "components/common/Button"
import * as styles from "../styles"

type Position = { left: number; top: number }
export type AnchorPoint = { x: number; y: number }

type ColorPickerPopoverProps = {
  // Current colour value. Controlled — parent updates this on each onChange so
  // the surrounding UI reflects the live picker state.
  colorValue: string
  // Fires on every change as the user drags (no I/O — parent updates preview).
  onChange: (color: string) => void
  onCancel: () => void
  onSave: () => void
  // Where the user clicked (clientX/clientY). The popover positions itself
  // near this point regardless of the clicked element's size.
  anchorPoint: AnchorPoint
}

const POPOVER_WIDTH = 296
const POPOVER_HEIGHT = 360
const MARGIN = 8
const HEX_DIGITS_RE = /[^0-9a-fA-F]/g

const stripHash = (value: string): string => value.replace(/^#/, "")

// Inline pipette/eyedropper SVG — styled-icons doesn't ship one in the packs
// installed here, and pulling another icon library just for this is overkill.
const EyedropperIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2 22l4-1 12-12-3-3L3 18l-1 4z" />
    <path d="m14.5 5.5 4 4" />
    <path d="M16 3l5 5-2 2-5-5 2-2z" />
  </svg>
)

// react-colorful always returns 8-digit hex. Strip the trailing FF so colours
// that don't need alpha stay 6-digit in storage (matches brand-token shape).
const normaliseHex = (hex: string): string => {
  if (/^#[0-9a-fA-F]{6}[fF]{2}$/.test(hex)) return hex.slice(0, 7)
  return hex
}

// Pull the alpha as a 0–100 integer from a hex string. Returns 100 for 6-digit hex.
const alphaPercent = (hex: string): number => {
  if (hex.length !== 9) return 100
  const alphaByte = parseInt(hex.slice(7, 9), 16)
  return Math.round((alphaByte / 255) * 100)
}

// Replace alpha in a hex string with the given 0–100 percent. Returns 8-digit hex.
const withAlphaPercent = (hex: string, percent: number): string => {
  const clamped = Math.max(0, Math.min(100, percent))
  const alphaHex = Math.round((clamped / 100) * 255).toString(16).padStart(2, "0").toUpperCase()
  const rrggbb = hex.length >= 7 ? hex.slice(1, 7) : "000000"
  return `#${rrggbb}${alphaHex}`
}

// Position the popover near the click point. We anchor to the click (not the
// element rect) and only slide the popover up when it would overflow the
// bottom — never flip entirely above the click. Flipping puts a 360px-tall
// popover near the top of the viewport whenever the click is past the upper
// third, which feels disconnected from where the user clicked.
const positionFor = (point: AnchorPoint): Position => {
  const OFFSET = 12
  let left = point.x + OFFSET
  let top = point.y + OFFSET

  // Horizontal: flip to the left of the click if the right side overflows.
  if (left + POPOVER_WIDTH > window.innerWidth - MARGIN) {
    left = point.x - POPOVER_WIDTH - OFFSET
  }

  // Vertical: clamp to the bottom edge. The popover may overlap the click
  // point on tall popovers + low clicks, but stays anchored near the click.
  if (top + POPOVER_HEIGHT > window.innerHeight - MARGIN) {
    top = window.innerHeight - POPOVER_HEIGHT - MARGIN
  }
  return { left: Math.max(MARGIN, left), top: Math.max(MARGIN, top) }
}

export const ColorPickerPopover: React.FC<ColorPickerPopoverProps> = ({
  colorValue,
  onChange,
  onCancel,
  onSave,
  anchorPoint,
}) => {
  const hexInputRef = useRef<HTMLInputElement>(null)
  const [position, setPosition] = useState<Position>(() => positionFor(anchorPoint))
  const [isShaking, setIsShaking] = useState(false)
  // Local draft for the hex input. The input value is the bare hex digits
  // (no `#` — that's rendered as a static prefix). Any non-hex character a
  // user types or pastes is dropped before it ever lands in state. While
  // the input is focused we don't resync from upstream — that would
  // clobber what the user is typing as we push padded interpretations up.
  const [hexDraft, setHexDraft] = useState(() => stripHash(colorValue))

  // Recompute popover position once after mount in case the viewport shifted.
  useLayoutEffect(() => {
    setPosition(positionFor(anchorPoint))
  }, [anchorPoint])

  // Escape cancels a deliberate keypress, unlike an outside click, which is deliberately
  // NOT a cancel. The backdrop below keeps the rest of the page unreachable meanwhile.
  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel()
    }
    document.addEventListener("keydown", handleKey)
    return () => document.removeEventListener("keydown", handleKey)
  }, [onCancel])

  // Lock page scroll while open. The picker is viewport-fixed, so scrolling
  // would drift it away from the swatch that opened it; wheel events over the
  // fixed backdrop scroll-chain to the viewport, so locking the root is enough.
  useEffect(() => {
    const root = document.documentElement
    const previousOverflow = root.style.overflow
    root.style.overflow = "hidden"
    return () => {
      root.style.overflow = previousOverflow
    }
  }, [])

  // Resync the hex input draft when the parent's colorValue changes — but
  // only while the input isn't focused, so typing isn't clobbered.
  useEffect(() => {
    if (document.activeElement !== hexInputRef.current) setHexDraft(stripHash(colorValue))
  }, [colorValue])

  const handleHexChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(HEX_DIGITS_RE, "")
    setHexDraft(digits)
    if (digits.length === 0) return
    // Live-pad the typed digits to the next valid hex length (3, 6, or 8)
    // with trailing zeros so the picker + slide preview reflect intent on
    // every keystroke even at partial lengths. Matches Canva: "F" → red,
    // "FF" → yellow, "FFF" → white.
    const targetLength = digits.length <= 3 ? 3 : digits.length <= 6 ? 6 : 8
    const padded = digits.padEnd(targetLength, "0").toUpperCase()
    onChange(`#${padded}`)
  }

  const handlePickerChange = (next: string) => {
    onChange(normaliseHex(next))
  }

  const handleAlphaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const percent = parseInt(event.target.value, 10)
    if (Number.isNaN(percent)) return
    onChange(normaliseHex(withAlphaPercent(colorValue, percent)))
  }

  const handleEyedropper = async () => {
    const Picker = (window as any).EyeDropper
    if (!Picker) return
    try {
      const result = await new Picker().open()
      if (result?.sRGBHex) onChange(normaliseHex(result.sRGBHex.toUpperCase()))
    } catch {
      // User cancelled — no-op.
    }
  }

  const hasEyedropper = typeof (window as any).EyeDropper === "function"

  const popover = (
    <styles.ColorPickerBox
      $shake={isShaking}
      onAnimationEnd={() => setIsShaking(false)}
      style={{ ...position, width: POPOVER_WIDTH }}
      data-color-picker-popover
      onMouseDown={(event) => event.stopPropagation()}
    >
      <HexAlphaColorPicker color={colorValue} onChange={handlePickerChange} />
      <styles.ColorPickerToolbar>
        <styles.ColorPickerInputCluster>
          <styles.ColorPickerSwatch style={{ background: colorValue }} />
          <styles.ColorPickerHashPrefix aria-hidden>#</styles.ColorPickerHashPrefix>
          <styles.ColorPickerHexInput
            ref={hexInputRef}
            value={hexDraft}
            onChange={handleHexChange}
            onFocus={(event) => event.currentTarget.select()}
            onKeyDown={(event) => { if (event.key === "Enter") { event.currentTarget.blur() } }}
            spellCheck={false}
            maxLength={8}
          />
          <styles.ColorPickerClusterDivider />
          <styles.ColorPickerAlphaGroup>
            <styles.ColorPickerAlphaInput
              type="number"
              min={0}
              max={100}
              value={alphaPercent(colorValue)}
              onChange={handleAlphaChange}
            />
            <styles.ColorPickerPercentLabel>%</styles.ColorPickerPercentLabel>
          </styles.ColorPickerAlphaGroup>
        </styles.ColorPickerInputCluster>
        {hasEyedropper && (
          <styles.ColorPickerEyedropperButton type="button" onClick={handleEyedropper} title="Pick a colour from the screen">
            <EyedropperIcon />
          </styles.ColorPickerEyedropperButton>
        )}
      </styles.ColorPickerToolbar>
      <styles.ColorPickerFooter>
        <Button variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={onSave}>Save</Button>
      </styles.ColorPickerFooter>
    </styles.ColorPickerBox>
  )

  return createPortal(
    <>
      <styles.ColorPickerBackdrop
        data-color-picker-backdrop
        onMouseDown={(event) => {
          event.preventDefault()
          setIsShaking(true)
        }}
      />
      {popover}
    </>,
    document.body,
  )
}

export default ColorPickerPopover
